import { arrayMove } from "@dnd-kit/sortable";

import { isNotNullOrUndefined } from "@smart/itops-utils-basic";

import {
  GqlMatterLayout,
  GroupOptions,
  MatterField,
  NewFieldOptions,
} from "../../types";
import {
  ContactType,
  buildContactDetailsGroupField,
  getDefaultContactDetailsLabel,
  resolveStandardContactFields,
} from "../../utils";
import { mapMatterField } from "../mapped-fields-modal/helpers";

export const buildStandardNewFields = ({
  layout,
  matterFields,
  contactType,
}: {
  layout: GqlMatterLayout;
  matterFields: MatterField[];
  contactType: ContactType;
}): NewFieldOptions[] =>
  resolveStandardContactFields({ matterFields, contactType })
    .map(mapMatterField(layout))
    .filter(isNotNullOrUndefined);

export const buildContactDetailsGroup = ({
  layout,
  contactType,
}: {
  layout: GqlMatterLayout;
  contactType: ContactType;
}): GroupOptions => ({
  uri: undefined,
  type: "contactDetails",
  label: getDefaultContactDetailsLabel(layout.displayName || layout.name),
  layout: {
    id: layout.id,
    name: layout.name,
    providerId: layout.providerId,
    parentId: layout.parentId || undefined,
    parentName: layout.parentName || undefined,
    parentProviderId: layout.parentProviderId || undefined,
  },
  field: buildContactDetailsGroupField(contactType),
});

export const reorderFields = (
  fields: NewFieldOptions[],
  activeId: string,
  overId: string,
): NewFieldOptions[] => {
  const from = fields.findIndex((field) => field.uri === activeId);
  const to = fields.findIndex((field) => field.uri === overId);

  if (from < 0 || to < 0 || from === to) return fields;

  return arrayMove(fields, from, to);
};

export const setFieldLabel = (
  fields: NewFieldOptions[],
  uri: string,
  label: string,
): NewFieldOptions[] => {
  const index = fields.findIndex((field) => field.uri === uri);
  if (index < 0 || fields[index].label === label) return fields;

  return fields.map((field, i) => (i === index ? { ...field, label } : field));
};

export const commitFieldLabel = (
  fields: NewFieldOptions[],
  uri: string,
  fallback: string | undefined,
): NewFieldOptions[] => {
  const index = fields.findIndex((field) => field.uri === uri);
  if (index < 0 || fields[index].label?.trim()) return fields;

  return fallback ? setFieldLabel(fields, uri, fallback) : fields;
};

export const commitAllFieldLabels = (
  fields: NewFieldOptions[],
  originals: Record<string, string>,
): NewFieldOptions[] =>
  fields.reduce(
    (agg, field) => commitFieldLabel(agg, field.uri, originals[field.uri]),
    fields,
  );

export const removeField = (
  fields: NewFieldOptions[],
  uri: string,
): NewFieldOptions[] => {
  if (fields.length <= 1) return fields;

  const remaining = fields.filter((field) => field.uri !== uri);
  return remaining.length === fields.length ? fields : remaining;
};

export const fieldCountLabel = (count: number) =>
  `Add ${count} ${count === 1 ? "field" : "fields"}`;

export type CustomisedContactDetailsField = {
  fieldName: string;
  label: string;
};

export const toSavedContactDetailsFields = (
  fields: NewFieldOptions[],
): CustomisedContactDetailsField[] =>
  fields
    .map((field) => {
      const fieldName = field.field?.name;
      if (!fieldName) return undefined;
      return {
        fieldName,
        label: field.label || "",
      };
    })
    .filter(isNotNullOrUndefined);

export const areContactDetailsFieldSetsEqual = (
  a: CustomisedContactDetailsField[],
  b: CustomisedContactDetailsField[],
) =>
  a.length === b.length &&
  a.every(
    (field, index) =>
      field.fieldName === b[index]?.fieldName &&
      field.label === b[index]?.label,
  );

export const buildFieldsFromFieldSet = ({
  layout,
  matterFields,
  fields,
}: {
  layout: GqlMatterLayout;
  matterFields: MatterField[];
  fields: CustomisedContactDetailsField[];
}): NewFieldOptions[] =>
  fields
    .map(({ fieldName, label }) => {
      const matterField = matterFields.find(
        (field) => field.name === fieldName,
      );
      if (!matterField) return undefined;
      const mapped = mapMatterField(layout)(matterField);
      if (!mapped) return undefined;
      return { ...mapped, label };
    })
    .filter(isNotNullOrUndefined);

export const duplicateNameMessage =
  "A field set with this name already exists. Please enter another name.";

export const isDuplicateName = ({
  trimmed,
  initialName,
  existingNames,
}: {
  trimmed: string;
  initialName?: string;
  existingNames: string[];
}): boolean => {
  if (trimmed === (initialName || "").trim()) return false;

  return existingNames.some(
    (existing) =>
      existing.trim().toLocaleLowerCase() === trimmed.toLocaleLowerCase(),
  );
};
